import { QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { UseLazyQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { DebounceInterval } from 'model';
import { useCallback, useRef, useState } from 'react';
import { TimeoutId } from '@reduxjs/toolkit/dist/query/core/buildMiddleware/types';
import { BaseQueryType } from '../api/baseQueryWithReauth.js';

export function useQueryWithDebounce<ResultType, ArgType>(
	useLazyQuery: UseLazyQuery<QueryDefinition<ArgType, BaseQueryType, any, ResultType, string>>,
	debounceInterval: DebounceInterval
) {
	const [trigger, queryResult] = useLazyQuery();
	const timer = useRef<TimeoutId>();
	const [result, setResult] = useState<ReturnType<typeof trigger>>();
	const wrapper = useCallback(
		(args: ArgType) => {
			if (timer.current) {
				clearTimeout(timer.current);
			}
			timer.current = setTimeout(() => {
				result?.abort();
				setResult(trigger(args));
			}, debounceInterval);
		},
		[debounceInterval, result, trigger]
	);
	// Catch the AbortError and return a successful result, this is internal only and the result of cancelling a query in flight
	if (queryResult.isError && 'message' in queryResult.error && queryResult.error.message === 'Aborted') {
		return { trigger: wrapper, ...queryResult, error: undefined, isError: false };
	}
	return { trigger: wrapper, ...queryResult };
}
